import { FC } from 'react';
import { Button, Icon } from 'learning-panel-components';
import { translate } from 'translations';
import { useNavigate } from 'react-router-dom';
import { getColorByScore } from 'utils';
import styles from './previewPage.module.scss';

const PreviewPage: FC<PreviewPageProps> = (props: PreviewPageProps) => {
    const {
        name,
        description,
        potentialScore,
        duration,
        onScrollToRemediate,
        isReadOnly,
        userQuestions,
        totalScore,
        onViewDrill,
        attemptsPerQuestionAllowed,
        drillTakes
    } = props;

    const navigate = useNavigate();

    return (
        <div className={styles['lab-preview']}>
            <div className={styles.back} onClick={() => navigate(-1)}>
                <Icon name="back" />
                {translate('Back')}
            </div>
            <div className={styles.left}>
                <div className={styles.data}>
                    <div className={styles.top}>
                        <Icon name="vulnerabilityFix" />
                        {translate('VulnerabilityFix').toUpperCase()}
                    </div>
                    <div className={styles.name}>{name}</div>
                    <div className={styles.settings}>
                        <div className={styles['settings-item']}>
                            <div className={styles['earn-up-to']}>{translate('UpTo')}</div>
                            <Icon name="devCoin" />
                            <div className={styles.score}>{potentialScore.toLocaleString()}</div>
                        </div>
                        <hr />
                        <div className={styles['settings-item']}>
                            <Icon name="clock" />
                            <div className={styles.duration}> {`${(duration / 60).toFixed()} ${translate('Min')}`}</div>
                        </div>
                    </div>
                    <div className={styles.description}>{description}</div>
                </div>
            </div>
            <hr />
            {isReadOnly ? (
                <div className={styles['read-only-right']}>
                    <div className={styles.title}>{translate('YourResults')}</div>
                    <div className={styles['first-settings-section']}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/drillSettingsConnector.webp`}
                            alt="settings"
                            className={styles['drill-image']}
                        />
                        <div className={styles.item}>
                            <div className={styles.number}>
                                {`${userQuestions?.filter(question => question.percentages === 100).length} / ${
                                    userQuestions?.length
                                }`}
                            </div>
                            <div className={styles.description}>{translate('AnswersRate')}</div>
                        </div>
                        <hr />
                        <div className={styles.item}>
                            {userQuestions && (
                                <div className={styles.number}>{`${(
                                    userQuestions.reduce((sum, q) => sum + (q.percentages ?? 0), 0) /
                                    userQuestions?.length
                                ).toFixed(0)}%`}</div>
                            )}
                            <div className={styles.description}>{translate('TotalScore')}</div>
                        </div>
                        <hr />
                        <div className={styles.item}>
                            <div className={styles.number}>
                                <Icon name="devCoin" />
                                {totalScore?.toLocaleString()}
                            </div>
                            <div className={styles.description}>{translate('DevCoins')}</div>
                        </div>
                    </div>
                    <div className={styles['sub-title']}>{translate('ResultsDrillDown')}</div>
                    <div className={styles['settings-section']}>
                        {userQuestions?.map((question, index) => (
                            <>
                                <div key={index} className={styles.item}>
                                    <div
                                        className={styles.percentages}
                                        style={{ backgroundColor: getColorByScore(question.percentages) }}
                                    >{`${question.percentages || 0}%`}</div>
                                    <div className={styles.number}>{question.questionNumber}</div>
                                </div>
                                {userQuestions?.length - 1 !== index && <hr />}
                            </>
                        ))}
                    </div>
                    <div className={styles['settings-img']}>
                        <div className={styles.hr} />
                        <img src={`${process.env.PUBLIC_URL}/images/labSettings.webp`} alt="settings" />
                    </div>
                    <div className={styles['fix-that-code']}>{translate('ViewOnlyDrill')}</div>
                    <Button
                        text={translate('ViewDrill')}
                        onClick={() => onViewDrill?.()}
                        type="bigGradient"
                        lowerCase
                        className={styles['remediate-button']}
                    />
                </div>
            ) : (
                <div className={styles.right}>
                    <div className={styles.title}>{translate('DrillGuides')}</div>
                    <div className={styles['first-settings-section']}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/drillSettingsConnector.webp`}
                            alt="settings"
                            className={styles['drill-image']}
                        />
                        <div className={styles.item}>
                            <div className={styles.number}>{userQuestions?.length}</div>
                            <div className={styles.description}>{translate('Questions')}</div>
                        </div>
                        <hr />
                        <div className={styles.item}>
                            <div className={styles.number}>{drillTakes}</div>
                            <div className={styles.description}>{translate('Takes')}</div>
                        </div>
                    </div>
                    <div className={styles['sub-title']}>{translate('EachQuestionInTheDrillIncludes')}</div>
                    <div className={styles['settings-section']}>
                        <div className={styles.item}>
                            <div className={styles.number}>{attemptsPerQuestionAllowed}</div>
                            <div className={styles.description}>{translate('Attempts')}</div>
                        </div>
                        <hr />
                        <div className={styles.item}>
                            <div className={styles.number}>
                                <Icon name="pinkCheck" />
                            </div>
                            <div className={styles.description}>{translate('LearningAccessories')}</div>
                        </div>
                    </div>
                    <div className={styles['settings-img']}>
                        <div className={styles.hr} />
                        <img src={`${process.env.PUBLIC_URL}/images/labSettings.webp`} alt="settings" />
                    </div>
                    <div className={styles['fix-that-code']}>{translate('FixThatCode')}</div>
                    <Button
                        text={translate('RemediateNow')}
                        onClick={() => onScrollToRemediate?.()}
                        type="bigGradient"
                        lowerCase
                        className={styles['remediate-button']}
                    />
                </div>
            )}
        </div>
    );
};

interface PreviewPageProps {
    name: string;
    description: string;
    potentialScore: number;
    duration: number;
    onScrollToRemediate?: Function;
    isReadOnly?: boolean;
    totalScore?: number;
    userQuestions?: userQuestion[];
    onViewDrill?: Function;
    attemptsPerQuestionAllowed: number;
    drillTakes: number;
}

export interface userQuestion {
    questionNumber: number;
    percentages: number | undefined;
}

export default PreviewPage;
